/**
 * ============================================================================
 * USE DISPUTE HOOK
 * ============================================================================
 * 
 * Custom hook for dispute operations
 */

import { useState, useCallback } from 'react';
import * as disputeService from '../services/disputes';

export const useDispute = (disputeId = null) => {
  const [dispute, setDispute] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [offers, setOffers] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);

  /**
   * Create dispute
   */
  const createDispute = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.createDispute(data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to create dispute');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get dispute by ID
   */
  const getDispute = useCallback(async (id = disputeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.getDispute(id);
      setDispute(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get dispute');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * List disputes
   */
  const listDisputes = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.listDisputes(filters);
      setDisputes(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to list disputes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Respond to dispute
   */
  const respondToDispute = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.respondToDispute(id, data);
      if (id === disputeId) {
        setDispute(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to respond to dispute');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Add evidence
   */
  const addEvidence = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.addEvidence(id, data);
      if (id === disputeId) {
        await loadEvidence(id);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to add evidence');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Get dispute evidence
   */
  const loadEvidence = useCallback(async (id = disputeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.getDisputeEvidence(id);
      setEvidence(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load evidence');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Delete evidence
   */
  const deleteEvidence = useCallback(async (id, evidenceId) => {
    try {
      setLoading(true);
      setError(null);
      await disputeService.deleteEvidence(id, evidenceId);
      if (id === disputeId) {
        await loadEvidence(id);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete evidence');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId, loadEvidence]);

  /**
   * Make offer
   */
  const makeOffer = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.makeOffer(id, data);
      if (id === disputeId) {
        await loadOffers(id);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to make offer');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Get dispute offers
   */
  const loadOffers = useCallback(async (id = disputeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.getDisputeOffers(id);
      setOffers(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load offers');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Accept offer
   */
  const acceptOffer = useCallback(async (id, offerId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.acceptOffer(id, offerId);
      if (id === disputeId) {
        setDispute(response.data);
        await loadOffers(id);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to accept offer');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId, loadOffers]);

  /**
   * Reject offer
   */
  const rejectOffer = useCallback(async (id, offerId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.rejectOffer(id, offerId);
      if (id === disputeId) {
        await loadOffers(id);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to reject offer');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId, loadOffers]);

  /**
   * Respond to offer
   */
  const respondToOffer = useCallback(async (id, offerId, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.respondToOffer(id, offerId, data);
      if (id === disputeId) {
        await loadOffers(id);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to respond to offer');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId, loadOffers]);

  /**
   * Request mediation
   */
  const requestMediation = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.requestMediation(id, data);
      if (id === disputeId) {
        setDispute(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to request mediation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Send message
   */
  const sendMessage = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.sendMessage(id, data);
      if (id === disputeId) {
        await loadMessages(id);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to send message');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Get messages
   */
  const loadMessages = useCallback(async (id = disputeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.getMessages(id);
      setMessages(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load messages');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Withdraw dispute
   */
  const withdrawDispute = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.withdrawDispute(id, data);
      if (id === disputeId) {
        setDispute(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to withdraw dispute');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [disputeId]);

  /**
   * Get user dispute summary
   */
  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.getUserDisputeSummary();
      setSummary(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load summary');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get transaction disputes
   */
  const getTransactionDisputes = useCallback(async (transactionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.getTransactionDisputes(transactionId);
      setDisputes(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get transaction disputes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get dispute stats (admin)
   */
  const loadStats = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await disputeService.getDisputeStats(filters);
      setStats(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load dispute stats');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    dispute,
    disputes,
    summary,
    offers,
    evidence,
    messages,
    stats,
    loading,
    error,
    
    // Actions
    createDispute,
    getDispute,
    listDisputes,
    respondToDispute,
    addEvidence,
    loadEvidence,
    deleteEvidence,
    makeOffer,
    loadOffers,
    acceptOffer,
    rejectOffer,
    respondToOffer,
    requestMediation,
    sendMessage,
    loadMessages,
    withdrawDispute,
    loadSummary,
    getTransactionDisputes,
    loadStats,
    
    // Convenience
    refresh: disputeId ? () => getDispute(disputeId) : null,
  };
};

export default useDispute;
